import React from 'react';
import { Head, Link, router, useForm } from '@inertiajs/react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Plus, ChevronLeft, ChevronRight } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { Breadcrumbs } from '@/components/ui/breadcrumbs';

interface ParseJob {
  id?: number;
  name?: string;
  chunk_size: number;
  chunk_mode: string;
  chunking_strategy?: string;
  start_page: number;
  end_page: number;
  response_payload?: any;
  raw_chunks?: Array<{
    page_number: number;
    content: string;
  }>;
}

interface Document {
  id: number;
  name: string;
  page_count: number;
  parse_jobs: ParseJob[];
}

interface ParseJobFormProps {
  document: Document;
  parseJob: ParseJob;
  chunkModes: string[];
  chunkingStrategies: string[];
  previousParseJob?: ParseJob;
  nextParseJob?: ParseJob;
}

export default function ParseJobForm({ 
  document, 
  parseJob, 
  chunkModes,
  chunkingStrategies,
  previousParseJob,
  nextParseJob
}: ParseJobFormProps) {
  const { data, setData, post, put, processing, errors } = useForm({
    name: parseJob.name || '',
    chunk_size: parseJob.chunk_size || 3000,
    chunk_mode: parseJob.chunk_mode || '',
    chunking_strategy: parseJob.chunking_strategy || 'size_aware',
    start_page: parseJob.start_page || 1,
    end_page: parseJob.end_page || document.page_count
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (parseJob.id) {
      put(`/documents/${document.id}/parse_jobs/${parseJob.id}`);
    } else {
      post(`/documents/${document.id}/parse_jobs`);
    }
  };

  const isEditing = !!parseJob.id;
  const title = isEditing 
    ? `Edit Parse Job for ${document.name}` 
    : `New Parse Job for ${document.name}`;

  return (
    <>
      <Head title={title} />
      
      <div className="container mx-auto p-6 max-w-7xl">
        <div className="space-y-6">
          {/* Breadcrumbs */}
          <Breadcrumbs opsConsole
            items={[
              { label: document.name, href: `/documents/${document.id}` },
              { label: isEditing ? `Edit Parse Job` : 'New Parse Job', current: true }
            ]}
          />

          {/* Header */}
          <div className="flex items-center justify-between">
            <h1 className="text-3xl font-bold">{title}</h1>
            <Button asChild variant="outline">
              <Link href={`/documents/${document.id}`}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Document
              </Link>
            </Button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Form Fields */}
            <Card>
              <CardHeader>
                <CardTitle>Parse Job Configuration</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name">Name Parse Job</Label>
                    <Input
                      id="name"
                      value={data.name}
                      onChange={(e) => setData('name', e.target.value)}
                      className={errors.name ? 'border-red-500' : ''}
                    />
                    {errors.name && (
                      <p className="text-sm text-red-600">{errors.name}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="chunk_size">Chunk Size</Label>
                    <Input
                      id="chunk_size"
                      type="number"
                      value={data.chunk_size}
                      onChange={(e) => setData('chunk_size', parseInt(e.target.value))}
                      className={errors.chunk_size ? 'border-red-500' : ''}
                    />
                    {errors.chunk_size && (
                      <p className="text-sm text-red-600">{errors.chunk_size}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="chunk_mode">Chunk Mode</Label>
                    <Select
                      value={data.chunk_mode}
                      onValueChange={(value) => setData('chunk_mode', value)}
                    >
                      <SelectTrigger className={errors.chunk_mode ? 'border-red-500' : ''}>
                        <SelectValue placeholder="Select chunk mode" />
                      </SelectTrigger>
                      <SelectContent>
                        {chunkModes.map((mode) => (
                          <SelectItem key={mode} value={mode}>
                            {mode.charAt(0).toUpperCase() + mode.slice(1).replace('_', ' ')}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.chunk_mode && (
                      <p className="text-sm text-red-600">{errors.chunk_mode}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="chunking_strategy">Chunking Strategy</Label>
                    <Select
                      value={data.chunking_strategy}
                      onValueChange={(value) => setData('chunking_strategy', value)}
                    >
                      <SelectTrigger className={errors.chunking_strategy ? 'border-red-500' : ''}>
                        <SelectValue placeholder="Select chunking strategy" />
                      </SelectTrigger>
                      <SelectContent>
                        {chunkingStrategies.map((strategy) => (
                          <SelectItem key={strategy} value={strategy}>
                            {strategy === 'size_aware' ? 'Size-Aware Divisor' : 'Divisor-Based'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.chunking_strategy && (
                      <p className="text-sm text-red-600">{errors.chunking_strategy}</p>
                    )}
                    <p className="text-sm text-gray-500">
                      {data.chunking_strategy === 'size_aware' 
                        ? 'Creates properly-sized chunks (~3000 chars) with divisor metadata and intelligent continuation handling.'
                        : 'Creates chunks based purely on document divisors (sections, articles, etc).'}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="start_page">Start Page</Label>
                    <Input
                      id="start_page"
                      type="number"
                      min="1"
                      value={data.start_page}
                      onChange={(e) => setData('start_page', parseInt(e.target.value))}
                      className={errors.start_page ? 'border-red-500' : ''}
                    />
                    {errors.start_page && (
                      <p className="text-sm text-red-600">{errors.start_page}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="end_page">End Page</Label>
                    <Input
                      id="end_page"
                      type="number"
                      min="1"
                      max={document.page_count}
                      value={data.end_page}
                      onChange={(e) => setData('end_page', parseInt(e.target.value))}
                      className={errors.end_page ? 'border-red-500' : ''}
                    />
                    {errors.end_page && (
                      <p className="text-sm text-red-600">{errors.end_page}</p>
                    )}
                  </div>
                </div>

                <div className="flex justify-end mt-6">
                  <Button type="submit" disabled={processing}>
                    {processing ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Parse Response and Raw Chunks (only show if editing existing job) */}
            {isEditing && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Parse Response */}
                <Card>
                  <CardHeader>
                    <CardTitle>Parse Response</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {parseJob.response_payload ? (
                      <div className="bg-gray-50 border border-gray-200 rounded-md p-4 h-96 overflow-auto">
                        <pre className="text-sm whitespace-pre-wrap break-words">
                          {JSON.stringify(parseJob.response_payload, null, 2)}
                        </pre>
                      </div>
                    ) : (
                      <div className="bg-gray-50 border border-gray-200 rounded-md p-4 text-center text-gray-600">
                        No Reducto Parse Response available.
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Raw Chunks */}
                <Card>
                  <CardHeader>
                    <CardTitle>Raw Chunks</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {parseJob.raw_chunks && parseJob.raw_chunks.length > 0 ? (
                      <div className="border border-gray-200 rounded-md h-96 overflow-auto">
                        <div className="space-y-2 p-4">
                          {parseJob.raw_chunks.map((chunk, index) => (
                            <div
                              key={index}
                              className="border-b border-gray-100 pb-3 last:border-b-0"
                            >
                              <div className="flex items-start gap-3">
                                <div className="flex-shrink-0 bg-gray-100 px-2 py-1 rounded text-sm font-semibold">
                                  Page {chunk.page_number}
                                </div>
                                <div className="flex-1 text-sm whitespace-pre-wrap break-words">
                                  {chunk.content}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="bg-gray-50 border border-gray-200 rounded-md p-4 text-center text-gray-600">
                        No raw chunks available.
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
          </form>

          {/* Navigation (only show if editing existing job) */}
          {isEditing && (previousParseJob || nextParseJob) && (
            <>
              <Separator />
              <div className="flex justify-between items-center">
                <div>
                  {previousParseJob && (
                    <Button asChild variant="outline">
                      <Link href={`/documents/${document.id}/parse_jobs/${previousParseJob.id}/edit`}>
                        <ChevronLeft className="h-4 w-4 mr-2" />
                        Previous Parse Job
                      </Link>
                    </Button>
                  )}
                </div>
                <div>
                  {nextParseJob && (
                    <Button asChild variant="outline">
                      <Link href={`/documents/${document.id}/parse_jobs/${nextParseJob.id}/edit`}>
                        Next Parse Job
                        <ChevronRight className="h-4 w-4 ml-2" />
                      </Link>
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}